import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import client from "@/lib/api";
import { Loader2, Package, Truck, CheckCircle2, LinkIcon } from "lucide-react";

const LOGO = "/tilesmate-logo.png";

export default function SharePage() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-job", token],
    queryFn: async () => (await client.get(`/public/builder-jobs/${token}`)).data,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#F8FAFC] px-6 text-center" data-testid="share-unavailable">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><LinkIcon className="h-6 w-6" /></div>
        <h1 className="font-display text-xl font-bold text-ink">This link isn't available</h1>
        <p className="max-w-sm text-sm text-muted-foreground">The selections link may have been turned off. Please contact your TilesMate consultant for an up-to-date link.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="share-page">
      <header className="border-b-[3px] border-[#F5A623] bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-5">
          <img src={LOGO} alt="TilesMate" className="h-10 w-auto" />
          <div className="text-right">
            <div className="font-display text-sm font-bold text-ink">Selections Summary</div>
            <div className="text-xs text-muted-foreground">Prepared for {data.builder_name}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-8 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
          <h1 className="font-display text-2xl font-extrabold text-ink" data-testid="share-job-title">{data.job_title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{data.builder_name}{data.address ? ` · ${data.address}` : ""}</p>
        </div>

        <div className="space-y-6">
          {data.stages.map((st) => (
            <section key={st.key} data-testid={`share-stage-${st.key}`} className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
              <div className="flex items-center justify-between gap-3 bg-primary px-5 py-3 text-white">
                <h2 className="font-display text-base font-bold">{st.label}</h2>
                <div className="flex items-center gap-2">
                  {st.delivery_timeline && <span className="rounded-full border border-[#F5A623]/50 bg-[#F5A623]/20 px-2.5 py-0.5 text-[11px] font-semibold text-[#F5A623]">Delivery: {st.delivery_timeline}</span>}
                  {st.status === "done" && <span className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold"><CheckCircle2 className="h-3 w-3" />Completed</span>}
                </div>
              </div>
              {st.items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
                  <Package className="h-5 w-5" />No selections recorded yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {st.items.map((it, i) => (
                    <li key={i} className="flex items-center gap-4 p-4" data-testid={`share-item-${st.key}-${i}`}>
                      {it.image_url ? (
                        <img src={it.image_url} alt={it.name} className="h-16 w-16 shrink-0 rounded-lg border border-slate-200 object-cover" />
                      ) : (
                        <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-300"><Package className="h-5 w-5" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-ink">{it.name || "—"}</div>
                        {it.sku && <div className="text-xs text-muted-foreground">SKU {it.sku}</div>}
                        {it.notes && <div className="mt-0.5 text-xs text-muted-foreground">{it.notes}</div>}
                      </div>
                      <div className="shrink-0 text-sm font-semibold text-ink">Qty {it.qty}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-10 border-t border-slate-200 pt-5 text-center text-xs text-muted-foreground">
          <div className="flex items-center justify-center gap-1.5"><Truck className="h-3.5 w-3.5" />Delivery timelines are indicative.</div>
          <p className="mt-2">Selections summary prepared by TilesMate. Colours and finishes shown are indicative — please confirm final selections with your consultant.</p>
        </footer>
      </main>
    </div>
  );
}
